import { useState, useEffect } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  InfoCircleOutlined,
  WarningOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useFlutterwavePayment } from "../hooks/usePaymentInit";
import { useAuthUser } from "react-auth-kit";
import { AUTHKIT_STATE_KEY } from "config/authStorage";
import { refreshUserPermissions } from "utils/refreshUserPermissions";
import { useGetUserInfo } from "hooks/useGetUserInfo";

interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
}

interface BillingInfo {
  credit: number;
  daysLeft: number;
  newPrice: number;
  chargeToday: number;
  nextBilling: string;
}

interface Props {
  onBack: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  currentPlan: Plan;
  selectedPlan: Plan;
  billingInfo: BillingInfo;
  dataPreservationMessages: string[];
  billingCycle: "monthly" | "annual";
}

export const PlanChangePage = ({
  onBack,
  onConfirm,
  onCancel,
  currentPlan,
  selectedPlan,
  billingInfo,
  dataPreservationMessages,
  billingCycle,
}: Props) => {
  const { token } = useGetUserInfo();
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [showDowngradeSuccess, setShowDowngradeSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = useAuthUser();

  const isUpgrade = selectedPlan.price > currentPlan.price;
  const isDowngrade = selectedPlan.price < currentPlan.price;

  const { mutate: initiatePayment, isLoading: isPaymentLoading } =
    useFlutterwavePayment();

  const handleSuccessfulUpgrade = async () => {
    setShowUpgradeSuccess(true);

    const refreshed = await refreshUserPermissions(token);

    if (refreshed) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  };

  const refreshUserInfo = () => {
    const userDetails = auth();
    if (userDetails?.user) {
      localStorage.setItem(
        AUTHKIT_STATE_KEY,
        JSON.stringify({
          ...userDetails,
          user: {
            ...userDetails.user,
          },
        }),
      );
    }
    window.location.reload();
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      if (isUpgrade) {
        const paymentParams = {
          planId: selectedPlan.id,
          paymentGatewayId: 1,
          planDuration: billingCycle,
          isUpgrade: true,
        };

        initiatePayment(paymentParams, {
          onSuccess: async (response) => {
            await handleSuccessfulUpgrade();
          },
          onError: (error) => {
            console.error("Payment failed:", error);
            setLoading(false);
          },
        });
      } else {
        setShowDowngradeSuccess(true);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (showUpgradeSuccess || showDowngradeSuccess) {
      timer = setTimeout(() => {
        setShowUpgradeSuccess(false);
        setShowDowngradeSuccess(false);

        if (showDowngradeSuccess) {
          onConfirm();
        }
      }, 4000);
    }

    return () => clearTimeout(timer);
  }, [showUpgradeSuccess, showDowngradeSuccess, onConfirm]);

  return (
    <>
      <div className="overflow-auto bg-gray-50 p-6">
        <div className="text-sm text-gray-500 mb-6 flex items-center">
          <span onClick={onBack} className="cursor-pointer flex items-center">
            <ArrowLeftOutlined className="mr-1 text-xs" />
            Pricing
          </span>
          <span className="mx-2">/</span>
          <span className="cursor-pointer flex items-center" onClick={onCancel}>
            Choose Plan
          </span>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium cursor-pointer">
            {isUpgrade ? "Upgrade Plan" : "Downgrade Plan"}
          </span>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden w-full h-full">
          <div
            className={`h-1.5 ${isUpgrade ? "bg-[#0B0F5B]" : "bg-amber-500"}`}
          />

          <div className="p-8">
            <h1 className="text-2xl font-bold mb-2">
              {isUpgrade ? "Upgrade Your Plan" : "Downgrade Your Plan"}
            </h1>

            <div className="mb-6 text-sm text-gray-500">
              {currentPlan.name} →{" "}
              <span className="font-semibold text-[#0B0F5B]">
                {selectedPlan.name}
              </span>
            </div>

            {isDowngrade && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-2 items-start">
                    <WarningOutlined className="text-amber-600 mt-1" />
                    <div>
                      <p className="font-semibold text-amber-800 text-sm">
                        Before you downgrade
                      </p>
                      <p className="text-sm text-amber-700">
                        Your downgrade will take effect at the end of your
                        current billing cycle. You'll continue to have full
                        access until then.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-lg p-4 mb-6 flex gap-3">
                  <InfoCircleOutlined className="text-gray-500 mt-1" />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium">Downgrade effective date</p>
                    <p className="text-gray-500">
                      {billingInfo.nextBilling} – You'll keep {currentPlan.name}{" "}
                      access until then.
                    </p>
                  </div>
                </div>

                {/* Features you'll lose */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Features you'll lose:</h3>

                  <div className="space-y-3">
                    {currentPlan.features.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 border border-red-200 bg-red-50 rounded-lg px-4 py-3 text-sm"
                      >
                        <CloseOutlined className="text-red-500" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Info */}
                <div className="bg-gray-50 border rounded-lg p-4 mb-6">
                  <h3 className="font-semibold mb-3 text-sm">
                    What happens to your data?
                  </h3>

                  <div className="space-y-2 text-sm text-gray-700">
                    {dataPreservationMessages.map((message, i) => (
                      <div key={i} className="flex gap-2">
                        <CheckCircleFilled className="text-green-500 mt-1" />
                        {message}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Billing Box */}
            {isUpgrade && (
              <>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold mb-4">
                    Billing Impact Analysis
                  </h3>

                  <div className="flex justify-between text-sm mb-2">
                    <span>Credit ({billingInfo.daysLeft} days remaining)</span>
                    <span className="text-green-600">
                      -₦{billingInfo.credit}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm mb-4">
                    <span>New plan price</span>
                    <span>₦{billingInfo.newPrice}</span>
                  </div>

                  <div className="border-t pt-3 flex justify-between font-semibold">
                    <span>Charge today</span>
                    <span className="text-blue-600 text-lg">
                      ₦{billingInfo.chargeToday}
                    </span>
                  </div>
                </div>

                {/* Next billing */}
                <div className="bg-gray-50 border rounded-lg p-4 flex gap-3 mb-6">
                  <InfoCircleOutlined className="mt-1 text-gray-500" />
                  <p className="text-sm text-gray-700">
                    Next billing date: {billingInfo.nextBilling} – ₦
                    {billingInfo.newPrice}
                    /month
                  </p>
                </div>

                {/* Features you'll unlock */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">
                    What you'll unlock immediately:
                  </h3>

                  <div className="space-y-2">
                    {selectedPlan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircleFilled className="text-green-500 mt-1" />{" "}
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer notes */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm text-gray-600 space-y-1">
                  <p>• Payment processed immediately</p>
                  <p>• You can cancel anytime</p>
                  <p>• No long-term contracts</p>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-5 py-2 border rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {isUpgrade ? "Cancel" : "Keep Current Plan"}
              </button>

              <button
                disabled={loading || isPaymentLoading}
                className={`px-5 py-2 rounded-md text-white font-semibold transition-colors
  ${loading || isPaymentLoading ? "opacity-50 cursor-not-allowed" : ""}
  ${
    isUpgrade
      ? loading || isPaymentLoading
        ? "bg-[#0B0F5B]"
        : "bg-[#0B0F5B] hover:bg-[#0B0F5B]/90"
      : loading || isPaymentLoading
        ? "bg-red-600"
        : "bg-red-600 hover:bg-red-700"
  }
`}
                onClick={handleConfirm}
              >
                {isUpgrade
                  ? `Confirm Upgrade - ₦${billingInfo.chargeToday}`
                  : "Confirm Downgrade"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
